import { 
  RolePermissions, 
  ModuleName, 
  ModulePermissions,
} from '../types/permissions';

export const checkPermission = (
  userPermissions: RolePermissions,
  moduleName: ModuleName,
  action: keyof ModulePermissions
): boolean => {
  return userPermissions?.[moduleName]?.[action] === true;
};

// Check if user can view a module
export const canViewModule = (
  userPermissions: RolePermissions,
  moduleName: ModuleName
): boolean => {
  return checkPermission(userPermissions, moduleName, "view");
};

export const canAddToModule = (
  userPermissions: RolePermissions,
  moduleName: ModuleName
): boolean => {
  return checkPermission(userPermissions, moduleName, "add");
};


// Check if user can edit in a module
export const canEditInModule = (
  userPermissions: RolePermissions,
  moduleName: ModuleName
): boolean => {
  return checkPermission(userPermissions, moduleName, "edit");
};

// Check if user can delete from a module
export const canDeleteFromModule = (
  userPermissions: RolePermissions,
  moduleName: ModuleName
): boolean => {
  return checkPermission(userPermissions, moduleName, "delete");
};

// Check if user can approve in a module
export const canApproveInModule = (
  userPermissions: RolePermissions,
  moduleName: ModuleName
): boolean => {
  return checkPermission(userPermissions, moduleName, "approve");
};

// Check if user can manage users for a module
export const canManageModuleUsers = (
  userPermissions: RolePermissions,
  moduleName: ModuleName
): boolean => {
  return checkPermission(userPermissions, moduleName, "manageUsers");
};

// Get all accessible modules for a user
export const getAccessibleModules = (
  userPermissions: RolePermissions
): ModuleName[] => {
  return Object.keys(userPermissions).filter(
    (moduleName) => userPermissions[moduleName]?.view === true
  ) as ModuleName[];
};

// Get all modules where user can manage users
export const getModulesWithUserManagement = (
  userPermissions: RolePermissions
): ModuleName[] => {
  return Object.keys(userPermissions).filter(
    (moduleName) => userPermissions[moduleName]?.manageUsers === true
  ) as ModuleName[];
};

// Check if user has access to User Management section
export const hasUserManagementAccess = (
  userPermissions: RolePermissions
): boolean => {
  return getModulesWithUserManagement(userPermissions).length > 0;
};

// Check if user has access to Role Management section
export const hasRoleManagementAccess = (
  userPermissions: RolePermissions
): boolean => {
  return userPermissions["role-management"]?.view === true;
};

// Check if user is super admin
export const isSuperAdmin = (role?: string): boolean => {
  if (!role) return false;
  const r = role.toString().trim().toLowerCase();
  return (
    r === "super-admin" ||
    r === "super admin" ||
    r === "superadmin" ||
    r === "sa" ||
    r === "super"
  );
};
